import { Switch as HeadlessSwitch } from '@headlessui/react';
import React, { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';

import { ToggleLockIcon } from 'assets/icons/library/ToggleLockIcon';

export type ToggleState = 'default' | 'checked' | 'locked' | 'disabled';

export interface ToggleProps {
  checkedBackgroundColor?: string;
  className?: string;
  onChange: () => void;
  onClick?: () => void;
  size?: 'small' | 'medium';
  state: ToggleState;
  style?: CSSProperties;
}

/**
 * Toggle component that wraps HeadlessUI Switch with custom styling.
 * Supports multiple states including locked and disabled states.
 *
 * The onClick callback will fire even when the toggle is disabled or locked,
 * achieved through an overlay div that captures clicks in those states.
 */
export const Toggle: React.FC<ToggleProps> = ({ checkedBackgroundColor, className, onChange, onClick, state, style, size = 'medium' }) => {
  const disabled = state === 'disabled' || state === 'locked';
  const isChecked = state === 'checked';
  const isLocked = state === 'locked';

  const handleClick = () => {
    onClick?.();
  };

  return (
    <div className="relative">
      <HeadlessSwitch checked={isChecked ?? false} disabled={disabled} onChange={onChange} style={style} className={className}>
        <span
          className={twMerge(
            'h-6 w-10 rounded-full flex p-spl-1 bg-bg-tert-10-80 transition-all',
            size === 'small' && 'w-8 h-5',
            isChecked && (checkedBackgroundColor ?? 'bg-bg-prim-cta'),
            'outline-none focus:ring-transparent focus:ring-offset-0 focus:ring-0',
            'focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-prim-w-b focus-visible:ring-[#1D76ED]'
          )}
        >
          <span
            className={twMerge(
              'h-5 w-5 block relative transition rounded-full duration-300 ease-in-out transform bg-icn-txt-white',
              size === 'small' && 'size-4',
              isChecked && (size === 'medium' ? 'translate-x-[80%]' : 'translate-x-[75%]'),
              disabled && 'bg-icn-txt-tert'
            )}
          >
            {isLocked && <ToggleLockIcon />}
          </span>
        </span>
      </HeadlessSwitch>
      {disabled && <div className="absolute inset-0 z-10 cursor-pointer" onClick={handleClick} />}
    </div>
  );
};
